// apps/cli/src/lib/chat-invite.ts
//
// Where the install-time chat lives, and why it is CONFIG rather than a literal.
//
// The founder waits ~30 minutes for the cloud installer. We point them at the
// hosted Ezra on m8t.run for that wait. The link is a dedicated, revocable,
// redemption-capped invite, so it has to be rotatable WITHOUT shipping a new CLI:
// a hardcoded URL would make "rotate the invite" a release, and an invite nobody
// can rotate is an invite nobody will revoke.
//
// So it is one small JSON file in the public agent repository, read anonymously.
// Rotating it is a one-field pull request against that repo — a data change,
// never a cut. It ships DORMANT (`enabled: false`) so the degrade path below is
// the tested default and turning chat on later is that same one-field change.
//
// It is not a secret and must never be treated as one (no vault machinery): it is
// revocable, capped, and gates only a hosted chat.
//
// THE RULE THIS FILE EXISTS TO KEEP: the install NEVER blocks or fails on chat.
// Every failure mode here — unreachable, slow, 404, malformed, dormant, bad URL,
// no browser — resolves to one honest line and an exit code of zero.

import { EZRA_REPO } from "./ezra-guides.js";
import { tryOpenUrl } from "./gh-helpers.js";

/** Path within the agent repository. Kept next to the runbook it belongs to. */
export const CHAT_INVITE_PATH = ".m8t/chat-invite.json";

/** Long enough for a slow link, short enough that nobody notices us giving up. */
export const CHAT_INVITE_TIMEOUT_MS = 3_000;

export type InviteUnavailableReason =
  /** Network refused, DNS failed, or we aborted on the timeout. */
  | "unreachable"
  /** The file is not there, or the repository answered an error. */
  | "http-error"
  /** Not JSON, or not an object. */
  | "malformed"
  /** A schemaVersion this CLI does not know how to read. */
  | "unknown-schema"
  /** Present and deliberately switched off — the shipped default. */
  | "disabled"
  /** Enabled but carries no address. */
  | "no-url"
  /** Enabled, but the address is not one we are willing to hand a browser. */
  | "insecure-url";

/**
 * Hosts this CLI will open from remote config.
 *
 * The URL comes from a file in a public repository and ends up in the founder's
 * browser on our say-so, under copy that calls it "our Ezra". Checking the protocol
 * alone would let a compromised or fat-fingered config point that sentence anywhere.
 * The invite is ours; the allowlist costs nothing and bounds the blast radius.
 */
const ALLOWED_INVITE_HOSTS = ["m8t.run"];

function hostAllowed(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return ALLOWED_INVITE_HOSTS.some((allowed) => host === allowed || host.endsWith(`.${allowed}`));
}

/**
 * Characters that cmd.exe re-parses.
 *
 * `tryOpenUrl` opens a URL on Windows via `cmd /c start "" <url>`, and Node only
 * quotes an argument containing a space, tab or quote — so `&` in an otherwise
 * perfectly legitimate URL truncates it, and a hostile one is command execution.
 * Fixing the opener is a change to a helper the GitHub App flow also depends on and
 * needs a Windows host to verify, so this refuses such a URL instead: degrading
 * honestly beats opening a truncated address, and our own invites are path-shaped.
 */
const SHELL_UNSAFE = /[&|^<>"`$%\\\s]/;

export type ChatInvite =
  | { ok: true; url: string }
  | { ok: false; reason: InviteUnavailableReason };

export function chatInviteUrl(): string {
  return `https://raw.githubusercontent.com/${EZRA_REPO}/main/${CHAT_INVITE_PATH}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Resolve the invite. Never throws, never rejects, never blocks past the timeout.
 *
 * The reason codes are distinct on purpose even though the founder only ever sees
 * one line: an operator diagnosing "why did nobody get the chat link" needs to tell
 * "we shipped it dormant" apart from "the founder's network ate it".
 */
export async function fetchChatInvite(deps: {
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
} = {}): Promise<ChatInvite> {
  const fetchImpl = deps.fetchImpl ?? fetch;
  let parsed: unknown;
  try {
    const res = await fetchImpl(chatInviteUrl(), {
      headers: { accept: "application/json" },
      signal: AbortSignal.timeout(deps.timeoutMs ?? CHAT_INVITE_TIMEOUT_MS),
    });
    if (!res.ok) return { ok: false, reason: "http-error" };
    const raw = await res.text();
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { ok: false, reason: "malformed" };
    }
  } catch {
    // Network failure, DNS, TLS, abort — all one thing to a founder, and none of
    // them are worth a stack trace in the middle of an install.
    return { ok: false, reason: "unreachable" };
  }

  if (!isRecord(parsed)) return { ok: false, reason: "malformed" };
  if (parsed.schemaVersion !== 1) return { ok: false, reason: "unknown-schema" };
  // Checked before the URL so a dormant invite reports as dormant even while it
  // carries a real address — that is the state we ship, and it is not an error.
  if (parsed.enabled !== true) return { ok: false, reason: "disabled" };
  const raw = typeof parsed.inviteUrl === "string" ? parsed.inviteUrl.trim() : "";
  if (!raw) return { ok: false, reason: "no-url" };
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(raw);
  } catch {
    return { ok: false, reason: "insecure-url" };
  }
  // We are about to hand a founder's browser to this address on our say-so.
  if (parsedUrl.protocol !== "https:") return { ok: false, reason: "insecure-url" };
  if (!hostAllowed(parsedUrl.hostname)) return { ok: false, reason: "insecure-url" };
  // `href`, not the raw string: normalised, so control characters and odd encodings
  // in the config cannot reach a terminal or an opener unchanged.
  const url = parsedUrl.href;
  if (SHELL_UNSAFE.test(url)) return { ok: false, reason: "insecure-url" };
  return { ok: true, url };
}

/**
 * The pointer copy.
 *
 * The ours/yours possessives ARE the disclaimer — a founder reading "our Ezra"
 * against "your real Ezra" already knows which one this is. No capability list, no
 * paragraph about what it cannot see, and no duration claim: the install takes as
 * long as it takes, and a number here would be a promise.
 */
export function renderChatPointer(url: string, opts: { print: boolean }): string {
  const lead = "While your real Ezra installs in your cloud, talk to our Ezra to see what he can do for you.\n";
  return opts.print ? `${lead}  Open this: ${url}\n` : `${lead}  ${url}\n`;
}

/** One line, every failure mode. Its job is to stop a missing chat reading as a broken install. */
export const CHAT_UNAVAILABLE_LINE = "  Chat with Ezra isn't available right now — your install is unaffected.\n";

/**
 * Resolve the invite and hand it to the founder's browser.
 *
 * Returns the text to print. Never throws — an opener that fails (headless box,
 * no `xdg-open`, SSH session) still leaves the founder the URL on screen, which
 * is the outcome that actually matters.
 */
export async function openChatInvite(deps: {
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
  open?: (url: string) => void | Promise<void>;
  print?: boolean;
} = {}): Promise<string> {
  const invite = await fetchChatInvite(deps);
  if (!invite.ok) return CHAT_UNAVAILABLE_LINE;
  const print = deps.print === true;
  if (!print) {
    const open = deps.open ?? tryOpenUrl;
    try {
      await open(invite.url);
    } catch { /* best-effort: the URL is printed either way */ }
  }
  return renderChatPointer(invite.url, { print });
}
